import { Injectable } from '@angular/core';

export interface LogEntry {
  timestamp: string;
  level: 'LOG' | 'INFO' | 'WARN' | 'ERROR';
  messages: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ConsoleLoggerService {
  private maxLogs = 100;
  private logs: LogEntry[] = [];
  private isHooked = false;

  // Preserve original console functions
  private originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  };

  constructor() {
    this.initHook();
  }

  public initHook(): void {
    if (this.isHooked) return;
    this.isHooked = true;

    console.log = (...args: any[]) => {
      this.addLog('LOG', args);
      this.originalConsole.log(...args);
    };

    console.info = (...args: any[]) => {
      this.addLog('INFO', args);
      this.originalConsole.info(...args);
    };

    console.warn = (...args: any[]) => {
      this.addLog('WARN', args);
      this.originalConsole.warn(...args);
    };

    console.error = (...args: any[]) => {
      this.addLog('ERROR', args);
      this.originalConsole.error(...args);
    };
  }

  private addLog(level: 'LOG' | 'INFO' | 'WARN' | 'ERROR', args: any[]): void {
    const formatted = args.map(arg => this.formatArg(arg));
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      messages: formatted
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  private formatArg(arg: any): string {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
    }

    try {
      const seen = new WeakSet();
      return JSON.stringify(arg, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular Reference]';
          }
          seen.add(value);
        }
        return value;
      });
    } catch {
      return String(arg);
    }
  }

  public getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  public getFormattedLogs(count: number = 50): string {
    return this.getRecentLogs(count)
      .map(l => `[${l.timestamp.substring(11, 19)}] [${l.level}] ${l.messages.join(' ')}`)
      .join('\n');
  }

  public clear(): void {
    this.logs = [];
  }
}
